import type { StaffNavContext } from './staff-nav-context'
import {
  collectNavPaths,
  countNavLeaves,
  isNavGroup,
  pushIfPresent,
  serializeNavTree,
  type StaffNavGroup,
  type StaffNavLeaf,
  type StaffNavNode,
} from './nav-node'
import { buildAccountGroup } from './sections/account'
import { buildAccountsDeskGroup } from './sections/accounts-desk'
import { buildAdminGroup } from './sections/admin'
import { buildClientGroup } from './sections/client'
import { buildDocumentFilingGroup } from './sections/document-filing'
import { buildHrGroup } from './sections/hr'
import { buildOperationsGroup } from './sections/operations'
import { buildPayrollOpsGroup } from './sections/payroll'
import { buildProGroup } from './sections/pro'
import { buildRecruitmentGroup } from './sections/recruitment'
import { buildSalesGroup } from './sections/sales'
import { buildTeamGroup } from './sections/team'
import { buildThinAdminTree } from './sections/thin-admin'

export type { StaffNavGroup, StaffNavLeaf, StaffNavNode }
export {
  collectNavPaths,
  countNavLeaves,
  isNavGroup,
  serializeNavTree,
}

export {
  filterNavTree,
  expandedGroupIdsForPath,
  mergeExpandedGroupIds,
  singleGroupIdsToExpand,
  workGroupIds,
} from './nav-tree-utils'

/** Capability-filtered sidebar — root is teams/sections only (no loose field leaves). */
export function buildStaffNavTree(ctx: StaffNavContext): StaffNavNode[] {
  if (ctx.isThinAdmin) {
    return buildThinAdminTree(ctx)
  }

  if (ctx.isDocumentFilingOnly) {
    const tree: StaffNavNode[] = []
    pushIfPresent(tree, buildDocumentFilingGroup(ctx))
    pushIfPresent(tree, ctx.showAccountSection ? buildAccountGroup(ctx) : null)
    return tree
  }

  if (ctx.isClientOnly) {
    const tree: StaffNavNode[] = []
    pushIfPresent(tree, buildClientGroup(ctx))
    pushIfPresent(tree, ctx.showAccountSection ? buildAccountGroup(ctx) : null)
    return tree
  }

  const tree: StaffNavNode[] = []

  pushIfPresent(tree, buildRecruitmentGroup(ctx))
  pushIfPresent(tree, buildHrGroup(ctx))
  pushIfPresent(tree, buildSalesGroup(ctx))
  pushIfPresent(tree, buildTeamGroup(ctx))
  pushIfPresent(tree, buildPayrollOpsGroup(ctx))
  pushIfPresent(tree, buildAccountsDeskGroup(ctx))
  pushIfPresent(tree, buildProGroup(ctx))
  pushIfPresent(tree, buildOperationsGroup(ctx))
  pushIfPresent(tree, buildDocumentFilingGroup(ctx))
  pushIfPresent(tree, buildAdminGroup(ctx))
  pushIfPresent(tree, buildClientGroup(ctx))
  pushIfPresent(tree, ctx.showAccountSection ? buildAccountGroup(ctx) : null)

  return tree
}

/** Work modules only — Account is pinned separately in the sidebar shell. */
export function splitAccountFromTree(tree: StaffNavNode[]): {
  main: StaffNavNode[]
  account: StaffNavGroup | null
} {
  let account: StaffNavGroup | null = null
  const main: StaffNavNode[] = []
  for (const node of tree) {
    if (node.kind === 'group' && node.id === 'account') {
      account = node
    } else {
      main.push(node)
    }
  }
  return { main, account }
}
